import { useCallback, useRef, useState } from "react";
import { env } from "@/lib/env";
import { isSupabaseConfigured } from "@/lib/supabase";
import { getSupabase } from "@/lib/supabase";
import { sseFromPost } from "@/lib/sse";
import { useEntitlements, usePaywall } from "@/features/paywall";
import {
  useGenerationErrorHandler,
  type GenerationError,
} from "@/features/menu/generationErrors";
import { useTranslation } from "react-i18next";
import type { BrigadeEvent, UsagePayload, UsageSummary } from "@/types/brigade";
import type { MenuPlan, PremiumFilters } from "@/types/menu";

export interface BrigadeStreamState {
  running: boolean;
  events: BrigadeEvent[];
  currentRole: string | null;
  completedRoles: Set<string>;
  plan: MenuPlan | null;
  /** Translated, user-readable error message (never raw JSON). */
  error: string | null;
  /** Structured error (HTTP status, backend code, kind) for callers that
   * need to branch on it. Null for in-stream `failed` frames. */
  errorInfo: GenerationError | null;
  /** Latest usage frame per role — keys narrow to BrigadeRole values seen. */
  usageByRole: Partial<Record<string, UsagePayload>>;
  /** Running totals across all observed usage frames. */
  usageRollup: UsageSummary;
  /** Final per-run summary, attached to the terminal `completed` frame. */
  usageSummary: UsageSummary | null;
}

const EMPTY_ROLLUP: UsageSummary = {
  calls: 0,
  input_tokens: 0,
  cached_tokens: 0,
  output_tokens: 0,
  total_tokens: 0,
  duration_seconds: 0,
  estimated_cost_usd: null,
};

const INITIAL: BrigadeStreamState = {
  running: false,
  events: [],
  currentRole: null,
  completedRoles: new Set(),
  plan: null,
  error: null,
  errorInfo: null,
  usageByRole: {},
  usageRollup: EMPTY_ROLLUP,
  usageSummary: null,
};

/**
 * Live SSE consumer for `POST /api/v1/menu/generate/stream`.
 *
 * Tracks which brigade role is active and which have finished, so the
 * UI can render a checklist like:
 *   ✓ Nutritionniste
 *   ▸ Chef
 *   · Sous-chef
 *   · Contrôle qualité
 */
export interface StartOptions {
  premium?: PremiumFilters | null;
  generation_mode?: "personalised" | "surprise";
  theme?: string | null;
  /** Explicit ISO week_start (YYYY-MM-DD). When set, bypasses the backend's
   * day-of-week auto-resolve logic so the ↻ button can always force a
   * specific week regardless of the generation window. */
  week_start?: string | null;
}

export function useBrigadeStream(): BrigadeStreamState & {
  start: (opts?: StartOptions) => Promise<void>;
  abort: () => void;
  reset: () => void;
} {
  const [state, setState] = useState<BrigadeStreamState>(INITIAL);
  const abortRef = useRef<AbortController | null>(null);
  const { t } = useTranslation();
  const { isReadOnly } = useEntitlements();
  const { open: openPaywall } = usePaywall();
  const handleError = useGenerationErrorHandler();

  const start = useCallback(
    async (opts: StartOptions = {}) => {
      // Read-only users (free quota used, no subscription) would get a 402
      // from the generation gate: open the paywall straight away instead of
      // spending a round-trip. The server stays the source of truth.
      if (isReadOnly) {
        openPaywall({ variant: "read_only" });
        return;
      }

      abortRef.current?.abort();
      const controller = new AbortController();
      abortRef.current = controller;

      setState({ ...INITIAL, running: true });

      // Strip empty / falsy fields so the backend prompt builder cleanly
      // skips them (premium_clause returns "" for all-default filters).
      const premium = opts.premium ? cleanPremium(opts.premium) : null;
      const body: Record<string, unknown> = {};
      if (premium) body.premium = premium;
      if (opts.generation_mode && opts.generation_mode !== "personalised") {
        body.generation_mode = opts.generation_mode;
      }
      if (opts.theme && opts.theme !== "surprise") body.theme = opts.theme;
      if (opts.week_start) body.week_start = opts.week_start;

      try {
        const headers: Record<string, string> = {};
        if (isSupabaseConfigured()) {
          try {
            const {
              data: { session },
            } = await getSupabase().auth.getSession();
            if (session?.access_token) {
              headers.Authorization = `Bearer ${session.access_token}`;
            }
          } catch {
            // Session unavailable — proceed without auth header.
          }
        }

        let terminal = false;
        for await (const event of sseFromPost<BrigadeEvent>({
          url: `${env.API_BASE_URL}/api/v1/menu/generate/stream`,
          body,
          headers,
          signal: controller.signal,
        })) {
          setState((prev) => reduce(prev, event));
          if (event.type === "completed" || event.type === "failed") {
            terminal = true;
            break;
          }
        }
        // The server closed the stream without a `completed`/`failed` frame
        // (proxy timeout, crash): surface it instead of silently stopping.
        if (!terminal && !controller.signal.aborted) {
          setState((prev) => ({
            ...prev,
            running: false,
            currentRole: null,
            error: t("generation_errors.interrupted"),
          }));
        }
      } catch (e) {
        if (controller.signal.aborted) return;
        const info = handleError(e);
        setState((prev) => ({
          ...prev,
          running: false,
          currentRole: null,
          error: info.message,
          errorInfo: info,
        }));
      } finally {
        setState((prev) => ({ ...prev, running: false }));
      }
    },
    [isReadOnly, openPaywall, handleError, t],
  );

  const abort = useCallback(() => {
    abortRef.current?.abort();
    setState((prev) => ({ ...prev, running: false }));
  }, []);

  const reset = useCallback(() => {
    abortRef.current?.abort();
    setState(INITIAL);
  }, []);

  return { ...state, start, abort, reset };
}

function reduce(
  prev: BrigadeStreamState,
  event: BrigadeEvent,
): BrigadeStreamState {
  const events = [...prev.events, event];
  switch (event.type) {
    case "started":
      return { ...prev, events, currentRole: null };
    case "agent_invoked":
      return {
        ...prev,
        events,
        currentRole: event.role ?? prev.currentRole,
      };
    case "agent_completed": {
      const completedRoles = new Set(prev.completedRoles);
      if (event.role) completedRoles.add(event.role);
      return {
        ...prev,
        events,
        completedRoles,
        currentRole: prev.currentRole === event.role ? null : prev.currentRole,
      };
    }
    case "usage": {
      if (!event.usage) return { ...prev, events };
      const usage = event.usage;
      const usageByRole = { ...prev.usageByRole };
      if (event.role) {
        usageByRole[event.role] = usage;
      }
      const usageRollup: UsageSummary = {
        calls: prev.usageRollup.calls + 1,
        input_tokens: prev.usageRollup.input_tokens + usage.input_tokens,
        cached_tokens: prev.usageRollup.cached_tokens + usage.cached_tokens,
        output_tokens: prev.usageRollup.output_tokens + usage.output_tokens,
        total_tokens: prev.usageRollup.total_tokens + usage.total_tokens,
        duration_seconds:
          prev.usageRollup.duration_seconds + usage.duration_seconds,
        estimated_cost_usd: null,
      };
      return { ...prev, events, usageByRole, usageRollup };
    }
    case "completed": {
      const completedRoles = new Set(prev.completedRoles);
      // The terminal event arrives from the QA role.
      if (event.role) completedRoles.add(event.role);
      else completedRoles.add("qa");
      return {
        ...prev,
        events,
        completedRoles,
        plan: event.plan ?? null,
        usageSummary: event.usage_summary ?? prev.usageRollup,
        currentRole: null,
        running: false,
      };
    }
    case "failed":
      return {
        ...prev,
        events,
        error: event.error ?? "Erreur inconnue",
        running: false,
        currentRole: null,
      };
    case "progress":
    default:
      return { ...prev, events };
  }
}

function cleanPremium(p: PremiumFilters): PremiumFilters | null {
  const out: PremiumFilters = {};
  if (p.nutri_min) out.nutri_min = p.nutri_min;
  if (p.eco_min) out.eco_min = p.eco_min;
  if (p.seasonal_only) out.seasonal_only = true;
  if (typeof p.max_kcal_per_meal === "number") {
    out.max_kcal_per_meal = p.max_kcal_per_meal;
  }
  return Object.keys(out).length > 0 ? out : null;
}
